import { generatePath, useRouteMatch } from 'react-router-dom';
import dayjs from 'dayjs';
import classNames from 'classnames';

import {
  GenderEnum,
  ProductSaleStatus,
  ProductUserStatus,
  ProductVisibleStatus,
  SellingConditionEnum,
  TagsEnum,
} from '@models';
import { productDetails } from '@legacyUrls';
import { productReviews } from '@urls';
import { useAppContext } from '@contexts/AppContext';
import { Title } from '@ui/Title';
import { Link } from '@ui/Link';
import { Stars } from '@ui/Stars';
import { ProductStatusLabel } from '@components/ProductStatusLabel';
import { ProductDetailsFreeDeliveryTimerContainer } from '@containers/ProductDetailsFreeDeliveryTimer';
import { ProductDetailsBigSaleBanner } from '@components/ProductDetailsBigDiscountBanner';
import { YoutubeButton } from '@components/YoutubeButton';
import { ProductDetailsCreateOrderButtonsContainer } from '@containers/ProductDetailsCreateOrderButtons';
import { ProductDetailsSwapBanner } from '@components/ProductDetailsSwapBanner';
import { ProductDetailsPromoActionInfo } from '@components/ProductDetailsPromoActionInfo';
import { ProductFavoritesToggleContainer } from '@containers/ProductFavoritesToggle';
import { ProductDetailsQuestionContainer } from '@containers/product/ProductDetailsQuestion';
import { ProductDetailsBrandContainer } from '@containers/product/ProductDetailsBrand';

import { ProductDetailsAlerts } from '../ProductDetailsAlerts';
import { ProductDetailsPhotos } from '../ProductDetailsPhotos';
import { ProductDetailsPrice } from '../ProductDetailsPrice';

import s from './ProductDetailsInfo.sss';

const MAX_PRODUCT_NAME_SYMBOLS = 90;

interface MatchParams {
  namespace: string,
  category: string,
  slug: string,
  productId: string,
}

interface SaleLabel {
  date: string,
  status: ProductSaleStatus,
  price: number,
  sale?: {
    startDate: string,
    highlighting?: {
      title: string,
      icon: string,
      color: string,
    } | null,
  } | null,
}

interface Seller {
  id: number,
  gender?: GenderEnum | null,
  hasRecentActivity: boolean,
  fraudWarning: boolean,
  isUserRestrictedByBankId: boolean,
}

interface PromoAction {
  title: string,
  promoInfo?: string | null,
  icon?: string | null,
  isAdded: boolean,
}

interface Product {
  id: number,
  name: string,
  description: string,
  price?: number | null,
  oldPrice?: number | null,
  discountPercent?: number | null,
  canBeBought: boolean,
  rating?: number | null,
  ratingAmount?: number | null,
  photos: {
    idStr?: string | null,
    thumbnailUrl: string,
    originalUrl: string
  }[],
  withSafePayment: boolean,
  isUkrainian: boolean,
  isOwner: boolean,
  sellingCondition: SellingConditionEnum,
  tags: TagsEnum[],
  statusTitle?: ProductVisibleStatus | null,
  userStatusTitle: ProductUserStatus,
  videoOverviewId?: string | null,
  saleLabel: SaleLabel,
  seller: Seller,
  promoActions?: PromoAction[] | null,
  likes: number,
  isLiked: boolean,
  dialogId?: number | null,
  productCount: number,
}

interface Props {
  className?: string,
  product: Product,
  isChatEnabled?: boolean,
  isFavoritesEnabled?: boolean,
}

export const ProductDetailsInfo: React.FC<Props> = ({
  className,
  product,
}) => {
  const { isOutlet } = useAppContext();
  const match = useRouteMatch<MatchParams>(productDetails)!;

  const allReviewsPath = match ? generatePath(productReviews, {
    namespace: match.params.namespace,
    category: match.params.category,
    slug: match.params.slug,
    productId: match.params.productId,
  }) : null;

  const productName = product.name.trim();
  const productNameTrimmed = productName.length > MAX_PRODUCT_NAME_SYMBOLS
    ? `${productName.slice(0, MAX_PRODUCT_NAME_SYMBOLS).trim()}...`
    : product.name;

  let bigSaleStartDate = null;
  let bigSaleEndDate = null;

  if (
    product.saleLabel?.date
    && product.saleLabel?.sale?.startDate
    && product.saleLabel?.sale?.highlighting
    && dayjs().isBefore(product.saleLabel.date)
  ) {
    bigSaleStartDate = dayjs(product.saleLabel.sale.startDate);
    bigSaleEndDate = dayjs(product.saleLabel.date);
  }

  const status = product.userStatusTitle === ProductUserStatus.Hidden
    ? product.userStatusTitle
    : product.statusTitle;
  
  const activePromoAction = product.promoActions?.find((promoAction) => promoAction.isAdded);

  const isCanSendQuestion = (
    product.userStatusTitle !== ProductUserStatus.Hidden
    && product.statusTitle === ProductVisibleStatus.Available
    && !product.seller.isUserRestrictedByBankId
  );

  return (
    <div className={classNames(s.root, className)}>
      {(!product.seller.hasRecentActivity || product.seller.fraudWarning) && (
        <ProductDetailsAlerts
          className={s.alerts}
          product={product}
        />
      )}

      <section className={s.mainSection}>
        <div className={s.gallery}>
          <ProductDetailsPhotos
            photos={product.photos}
            product={product}
          />

          {!!product.videoOverviewId && (
            <YoutubeButton
              className={s.videoButton}
              videoId={product.videoOverviewId}
            />
          )}
        </div>

        <div className={s.mainInfo}>
          {!!status && (
            <ProductStatusLabel
              status={status}
              count={product.productCount}
            />
          )}

          <Title
            className={s.title}
            type='h1'
            bold={false}
          >
            {productNameTrimmed}
          </Title>

          {!!product.rating && !!product.ratingAmount && allReviewsPath && (
            <Link
              className={s.rating}
              theme='black'
              to={decodeURIComponent(allReviewsPath)}
            >
              <Stars
                value={product.rating}
                readOnly={true}
              />
              &nbsp;
              {`(${product.ratingAmount})`}
            </Link>
          )}

          <ProductDetailsBrandContainer
            className={s.brand}
            productId={product.id}
          />

          <ProductDetailsFreeDeliveryTimerContainer className={s.freedeliveryTimer} />

          {!!bigSaleStartDate && !!bigSaleEndDate && !!product.saleLabel.sale?.highlighting && (
            <div className={s.bigSaleBanner}>
              <ProductDetailsBigSaleBanner
                startDate={bigSaleStartDate}
                endDate={bigSaleEndDate}
                highlighting={product.saleLabel.sale.highlighting}
              />
            </div>
          )}

          <ProductDetailsPrice
            className={s.price}
            product={product}
          />

          {!product.isOwner && product.canBeBought && (
            <div className={s.createOrderButtons}>
              <ProductDetailsCreateOrderButtonsContainer />
            </div>
          )}

          {product.sellingCondition === SellingConditionEnum.Exchange && (
            <ProductDetailsSwapBanner />
          )}

          {!!activePromoAction && (
            <div className={s.promoActions}>
              <ProductDetailsPromoActionInfo
                title={activePromoAction.title}
                description={activePromoAction.promoInfo}
                iconUrl={activePromoAction.icon}
              />
            </div>
          )}

          {!product.isOwner && (
            <div className={s.favoritesWithChat}>
              {!isOutlet && (
                <ProductFavoritesToggleContainer
                  product={product}
                  type='ProductDetails'
                  isDetailed={true}
                />
              )}

              {isCanSendQuestion && (
                <ProductDetailsQuestionContainer
                  product={product}
                />
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
